/**
 * SecureEmbed Datastore Constants
 *
 * Shared datastore card definitions, colors, and utilities
 * Used by Dashboard and DatastoreBrowser pages
 */

import {
  Activity,
  Zap,
  Globe,
  Target,
  FileText,
  BookOpen,
  Building2,
  Heart,
  GraduationCap,
  Library,
  Landmark,
  Apple,
  Lightbulb,
  Shield,
  HeartPulse,
  TrendingUp,
  FileWarning,
  Triangle,
  ArrowRightLeft,
  Scale,
  Thermometer,
  ShieldAlert,
  CloudRain,
  Fingerprint,
  Cross,
  Languages,
  Search,
  StickyNote,
  ShieldCheck,
  Users,
  Award,
  LifeBuoy,
} from 'lucide-react';

export const DATASTORE_CARDS = [
  { key: 'aemo', label: 'AEMO Energy', icon: Activity },
  { key: 'aemo-duid', label: 'AEMO DUID', icon: Zap },
  { key: 'aemo-fcas', label: 'AEMO FCAS', icon: Zap },
  { key: 'bom', label: 'BOM Weather', icon: Globe },
  { key: 'aer', label: 'AER Regulatory', icon: Target },
  { key: 'regulatory', label: 'Regulatory Docs', icon: FileText },
  { key: 'scholarly', label: 'Scholarly Research', icon: BookOpen },
  { key: 'partner', label: 'Partner & Quantum Discovery', icon: Building2 },
  { key: 'medicine', label: 'Medicine', icon: Heart },
  { key: 'curriculum', label: 'Curriculum', icon: GraduationCap },
  { key: 'education-resources', label: 'Education Resources', icon: Library },
  { key: 'tertiary-resources', label: 'Tertiary Resources', icon: Landmark },
  { key: 'food-interactions', label: 'Food Interactions', icon: Apple },
  { key: 'innovateflow', label: 'InnovateFlow', icon: Lightbulb },
  { key: 'innovateflow-crm', label: 'InnovateFlow CRM', icon: Building2 },
  { key: 'collaborator-directory', label: 'Collaborator Directory', icon: Users },
  { key: 'insurance-apra-grf', label: 'APRA General Insurance', icon: Shield },
  { key: 'insurance-apra-lrf', label: 'APRA Life Insurance', icon: HeartPulse },
  { key: 'insurance-apra-hrf', label: 'APRA Health Insurance', icon: Heart },
  { key: 'insurance-premium-adequacy', label: 'Premium Adequacy', icon: TrendingUp },
  { key: 'insurance-claims-triage', label: 'Claims Triage', icon: FileWarning },
  { key: 'insurance-actuarial-triangles', label: 'Actuarial Triangles', icon: Triangle },
  { key: 'insurance-bordereaux', label: 'Cedant Bordereaux', icon: ArrowRightLeft },
  { key: 'insurance-afca-complaints', label: 'AFCA Complaints', icon: Scale },
  { key: 'insurance-state-schemes', label: 'State Schemes', icon: Landmark },
  { key: 'insurance-climate-projections', label: 'Climate Projections', icon: Thermometer },
  { key: 'insurance-cyber-threats', label: 'Cyber Threats', icon: ShieldAlert },
  { key: 'insurance-bom-weather', label: 'Insurance BOM Weather', icon: CloudRain },
  { key: 'audit-trail', label: 'Audit Trail', icon: Fingerprint },
  { key: 'theology-books', label: 'Theology Books', icon: Cross },
  { key: 'theology-verses', label: 'Theology Verses', icon: BookOpen },
  { key: 'theology-words', label: 'Theology Words', icon: Languages },
  { key: 'theology-lexicon', label: 'Theology Lexicon', icon: Languages },
  { key: 'theology-morphology-codes', label: 'Theology Morphology', icon: FileText },
  { key: 'theology-cross-references', label: 'Theology Cross-Refs', icon: ArrowRightLeft },
  { key: 'theology-word-index', label: 'Theology Word Index', icon: Search },
  { key: 'theology-translations', label: 'Theology Translations', icon: Languages },
  { key: 'theology-articles', label: 'Theology Articles', icon: BookOpen },
  { key: 'theology-article-verses', label: 'Theology Article-Verses', icon: Cross },
  { key: 'theology-user-notes', label: 'Theology User Notes', icon: StickyNote },
  { key: 'theology-note-passages', label: 'Theology Note Passages', icon: FileText },
  { key: 'theology-note-todos', label: 'Theology Note Todos', icon: Target },
  { key: 'theology-note-articles', label: 'Theology Note Articles', icon: BookOpen },
  { key: 'theology-user-article-tags', label: 'Theology User Tags', icon: Target },
  { key: 'theology-article-tag-map', label: 'Theology Tag Map', icon: ArrowRightLeft },
  { key: 'theology-user-state', label: 'Theology User State', icon: StickyNote },
  { key: 'theology-verse-embeddings', label: 'Theology Verse Embeddings', icon: Search },
  { key: 'theology-lexicon-embeddings', label: 'Theology Lexicon Embeddings', icon: Search },
  { key: 'theology-articles-embeddings', label: 'Theology Article Embeddings', icon: Search },
  { key: 'scm-embeddings', label: 'Security Control Mappings', icon: ShieldCheck },
  { key: 'patent-embeddings', label: 'Patents (Tier-1/Demo)', icon: Award },
  { key: 'patent-embeddings-fullset', label: 'Patents (Full Corpus)', icon: Award },
  { key: 'patent-citations', label: 'Patent Citations', icon: Award },
  { key: 'support', label: 'Support Tickets', icon: LifeBuoy },
];

export const DATASTORE_COLORS: Record<string, { hex: string }> = {
  aemo: { hex: '#F59E0B' },
  'aemo-duid': { hex: '#6366F1' },
  'aemo-fcas': { hex: '#14B8A6' },
  bom: { hex: '#3B82F6' },
  aer: { hex: '#10B981' },
  regulatory: { hex: '#059669' },
  scholarly: { hex: '#0EA5E9' },
  partner: { hex: '#EC4899' },
  medicine: { hex: '#F43F5E' },
  curriculum: { hex: '#F97316' },
  'education-resources': { hex: '#06B6D4' },
  'tertiary-resources': { hex: '#64748B' },
  'food-interactions': { hex: '#D35400' },
  innovateflow: { hex: '#0EA5E9' },
  'innovateflow-crm': { hex: '#8B5CF6' },
  'collaborator-directory': { hex: '#0D9488' },
  'insurance-apra-grf': { hex: '#1B2A4A' },
  'insurance-apra-lrf': { hex: '#243B53' },
  'insurance-apra-hrf': { hex: '#334E68' },
  'insurance-premium-adequacy': { hex: '#486581' },
  'insurance-claims-triage': { hex: '#C9A227' },
  'insurance-actuarial-triangles': { hex: '#A88420' },
  'insurance-bordereaux': { hex: '#86691A' },
  'insurance-afca-complaints': { hex: '#2563EB' },
  'insurance-state-schemes': { hex: '#1D4ED8' },
  'insurance-climate-projections': { hex: '#7C3AED' },
  'insurance-cyber-threats': { hex: '#6D28D9' },
  'insurance-bom-weather': { hex: '#5B21B6' },
  'audit-trail': { hex: '#059669' },
  'theology-books': { hex: '#7C3AED' },
  'theology-verses': { hex: '#8B5CF6' },
  'theology-words': { hex: '#A78BFA' },
  'theology-lexicon': { hex: '#6D28D9' },
  'theology-morphology-codes': { hex: '#5B21B6' },
  'theology-cross-references': { hex: '#4C1D95' },
  'theology-word-index': { hex: '#7C3AED' },
  'theology-translations': { hex: '#8B5CF6' },
  'theology-articles': { hex: '#8B4513' },
  'theology-article-verses': { hex: '#4F46E5' },
  'theology-user-notes': { hex: '#818CF8' },
  'theology-note-passages': { hex: '#6366F1' },
  'theology-note-todos': { hex: '#4F46E5' },
  'theology-note-articles': { hex: '#4338CA' },
  'theology-user-article-tags': { hex: '#3730A3' },
  'theology-article-tag-map': { hex: '#312E81' },
  'theology-user-state': { hex: '#818CF8' },
  'theology-verse-embeddings': { hex: '#7C3AED' },
  'theology-lexicon-embeddings': { hex: '#6D28D9' },
  'theology-articles-embeddings': { hex: '#5B21B6' },
  'scm-embeddings': { hex: '#4682B4' },
  'patent-embeddings': { hex: '#EA580C' },
  'patent-embeddings-fullset': { hex: '#F97316' },
  'patent-citations': { hex: '#C2410C' },
  support: { hex: '#0EA5E9' },
};

export const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

/** Column definitions per datastore for the browse page */
export const DATASTORE_COLUMNS: Record<string, { key: string; label: string }[]> = {
  aemo: [
    { key: 'region', label: 'Region' },
    { key: 'price', label: 'Price' },
    { key: 'date', label: 'Date' },
    { key: 'type', label: 'Type' },
    { key: 'source', label: 'Source' },
  ],
  'aemo-duid': [
    { key: 'region', label: 'Region' },
    { key: 'price', label: 'Price' },
    { key: 'date', label: 'Date' },
    { key: 'type', label: 'Type' },
    { key: 'source', label: 'Source' },
  ],
  'aemo-fcas': [
    { key: 'region', label: 'Region' },
    { key: 'price', label: 'Price' },
    { key: 'date', label: 'Date' },
    { key: 'type', label: 'Type' },
    { key: 'source', label: 'Source' },
  ],
  bom: [
    { key: 'station', label: 'Station' },
    { key: 'observation', label: 'Observation' },
    { key: 'date', label: 'Date' },
    { key: 'region', label: 'Region' },
  ],
  aer: [
    { key: 'title', label: 'Title' },
    { key: 'category', label: 'Category' },
    { key: 'date', label: 'Date' },
    { key: 'source', label: 'Source' },
  ],
  regulatory: [
    { key: 'title', label: 'Title' },
    { key: 'type', label: 'Type' },
    { key: 'date', label: 'Date' },
    { key: 'jurisdiction', label: 'Jurisdiction' },
  ],
  scholarly: [
    { key: 'title', label: 'Title' },
    { key: 'authors', label: 'Authors' },
    { key: 'journal', label: 'Journal' },
    { key: 'year', label: 'Year' },
    { key: 'citations', label: 'Citations' },
  ],
  partner: [
    { key: 'name', label: 'Name' },
    { key: 'country', label: 'Country' },
    { key: 'category', label: 'Category' },
    { key: 'facility_type', label: 'Facility Type' },
  ],
  medicine: [
    { key: 'name', label: 'Name' },
    { key: 'active_ingredient', label: 'Active Ingredient' },
    { key: 'form', label: 'Form' },
    { key: 'schedule', label: 'Schedule' },
    { key: 'source', label: 'Source' },
  ],
  curriculum: [
    { key: 'title', label: 'Title' },
    { key: 'subject', label: 'Subject' },
    { key: 'level', label: 'Level' },
    { key: 'state', label: 'State' },
  ],
  'education-resources': [
    { key: 'title', label: 'Title' },
    { key: 'type', label: 'Type' },
    { key: 'subject', label: 'Subject' },
    { key: 'level', label: 'Level' },
    { key: 'source', label: 'Source' },
  ],
  'tertiary-resources': [
    { key: 'title', label: 'Title' },
    { key: 'type', label: 'Type' },
    { key: 'institution', label: 'Institution' },
    { key: 'subject', label: 'Subject' },
    { key: 'source', label: 'Source' },
  ],
  'food-interactions': [
    { key: 'name', label: 'Food Name' },
    { key: 'category', label: 'AUSNUT Category' },
    { key: 'food_type', label: 'Type' },
    { key: 'nova_group', label: 'NOVA' },
    { key: 'source', label: 'Source' },
  ],
  innovateflow: [
    { key: 'title', label: 'Title' },
    { key: 'owner', label: 'Owner' },
    { key: 'type', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'topics', label: 'Topics' },
  ],
  'innovateflow-crm': [
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Entity Type' },
    { key: 'project', label: 'Project' },
    { key: 'source', label: 'Source' },
    { key: 'created', label: 'Created' },
  ],
  'collaborator-directory': [
    { key: 'name', label: 'Name' },
    { key: 'entity_type', label: 'Entity Type' },
    { key: 'region', label: 'Region' },
    { key: 'affiliation', label: 'Affiliation' },
    { key: 'has_embedding', label: 'Embedded' },
  ],
  'insurance-apra-grf': [
    { key: 'entity_name', label: 'Entity' },
    { key: 'reporting_period', label: 'Period' },
    { key: 'line_of_business', label: 'Line of Business' },
    { key: 'gross_written_premium', label: 'GWP ($M)' },
    { key: 'combined_ratio', label: 'Combined Ratio' },
  ],
  'insurance-apra-lrf': [
    { key: 'entity_name', label: 'Insurer' },
    { key: 'product_type', label: 'Product' },
    { key: 'in_force_premium', label: 'In-Force ($M)' },
    { key: 'claims_ratio', label: 'Claims Ratio' },
  ],
  'insurance-apra-hrf': [
    { key: 'fund_name', label: 'Fund' },
    { key: 'coverage_type', label: 'Coverage' },
    { key: 'lives_covered', label: 'Lives' },
    { key: 'gross_premium', label: 'Premium ($M)' },
  ],
  'insurance-premium-adequacy': [
    { key: 'insurer', label: 'Insurer' },
    { key: 'line_of_business', label: 'LoB' },
    { key: 'adequacy_status', label: 'Status' },
    { key: 'recommended_rate_change', label: 'Rate Change' },
  ],
  'insurance-claims-triage': [
    { key: 'claim_number', label: 'Claim #' },
    { key: 'line_of_business', label: 'LoB' },
    { key: 'peril', label: 'Peril' },
    { key: 'reserve_amount', label: 'Reserve ($)' },
    { key: 'status', label: 'Status' },
  ],
  'insurance-actuarial-triangles': [
    { key: 'line_of_business', label: 'LoB' },
    { key: 'accident_year', label: 'AY' },
    { key: 'development_month', label: 'Dev Month' },
    { key: 'development_factor', label: 'Dev Factor' },
  ],
  'insurance-bordereaux': [
    { key: 'cedant', label: 'Cedant' },
    { key: 'reinsurer', label: 'Reinsurer' },
    { key: 'treaty_type', label: 'Treaty Type' },
    { key: 'loss_ratio', label: 'Loss Ratio' },
  ],
  'insurance-afca-complaints': [
    { key: 'complaint_number', label: 'Complaint #' },
    { key: 'insurer', label: 'Insurer' },
    { key: 'product_type', label: 'Product' },
    { key: 'dispute_type', label: 'Dispute' },
    { key: 'outcome', label: 'Outcome' },
  ],
  'insurance-state-schemes': [
    { key: 'state', label: 'State' },
    { key: 'scheme_type', label: 'Scheme' },
    { key: 'period', label: 'Period' },
    { key: 'premium_pool_m', label: 'Pool ($M)' },
  ],
  'insurance-climate-projections': [
    { key: 'scenario', label: 'Scenario' },
    { key: 'time_horizon', label: 'Horizon' },
    { key: 'hazard_type', label: 'Hazard' },
    { key: 'severity', label: 'Severity' },
  ],
  'insurance-cyber-threats': [
    { key: 'threat_category', label: 'Category' },
    { key: 'threat_actor', label: 'Actor' },
    { key: 'targeted_sector', label: 'Sector' },
    { key: 'severity', label: 'Severity' },
  ],
  'insurance-bom-weather': [
    { key: 'station_name', label: 'Station' },
    { key: 'observation_type', label: 'Type' },
    { key: 'date', label: 'Date' },
    { key: 'severity', label: 'Severity' },
  ],
  'audit-trail': [
    { key: 'portal_id', label: 'Portal' },
    { key: 'event_type', label: 'Event Type' },
    { key: 'actor', label: 'Actor' },
    { key: 'resource', label: 'Resource' },
    { key: 'action', label: 'Action' },
  ],
  'theology-books': [
    { key: 'book_id', label: 'Book ID' },
    { key: 'name', label: 'Name' },
    { key: 'testament', label: 'Testament' },
    { key: 'chapters', label: 'Chapters' },
  ],
  'theology-verses': [
    { key: 'book_chapter', label: 'Book/Chapter' },
    { key: 'verse_num', label: 'Verse' },
    { key: 'text_kjv', label: 'KJV Text' },
    { key: 'testament', label: 'Testament' },
  ],
  'theology-words': [
    { key: 'verse_ref', label: 'Verse Ref' },
    { key: 'word_position', label: 'Position' },
    { key: 'lemma', label: 'Lemma' },
    { key: 'strongs_number', label: "Strong's #" },
  ],
  'theology-lexicon': [
    { key: 'strongs_number', label: "Strong's #" },
    { key: 'lemma', label: 'Lemma' },
    { key: 'definition', label: 'Definition' },
    { key: 'language', label: 'Language' },
  ],
  'theology-morphology-codes': [
    { key: 'code', label: 'Code' },
    { key: 'description', label: 'Description' },
    { key: 'language', label: 'Language' },
  ],
  'theology-cross-references': [
    { key: 'source_ref', label: 'Source' },
    { key: 'target_ref', label: 'Target' },
    { key: 'source', label: 'Data Source' },
  ],
  'theology-word-index': [
    { key: 'strongs_number', label: "Strong's #" },
    { key: 'verse_ref', label: 'Verse Ref' },
  ],
  'theology-translations': [
    { key: 'translation_id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'language', label: 'Language' },
    { key: 'license', label: 'License' },
  ],
  'theology-articles': [
    { key: 'title', label: 'Title' },
    { key: 'authors', label: 'Authors' },
    { key: 'journal', label: 'Journal' },
    { key: 'year', label: 'Year' },
    { key: 'doi', label: 'DOI' },
    { key: 'ot_nt_focus', label: 'OT/NT' },
    { key: 'tradition', label: 'Tradition' },
  ],
  'theology-article-verses': [
    { key: 'verse_ref', label: 'Verse Ref' },
    { key: 'article_id', label: 'Article ID' },
  ],
  'theology-user-notes': [
    { key: 'user_id', label: 'User' },
    { key: 'note_id', label: 'Note ID' },
    { key: 'title', label: 'Title' },
    { key: 'note_type', label: 'Type' },
  ],
  'theology-note-passages': [
    { key: 'note_id', label: 'Note ID' },
    { key: 'passage_ref', label: 'Passage Ref' },
  ],
  'theology-note-todos': [
    { key: 'note_id', label: 'Note ID' },
    { key: 'todo_id', label: 'Todo ID' },
    { key: 'status', label: 'Status' },
  ],
  'theology-note-articles': [
    { key: 'note_id', label: 'Note ID' },
    { key: 'article_id', label: 'Article ID' },
  ],
  'theology-user-article-tags': [
    { key: 'user_id', label: 'User' },
    { key: 'tag_id', label: 'Tag ID' },
    { key: 'label', label: 'Label' },
  ],
  'theology-article-tag-map': [
    { key: 'tag_id', label: 'Tag ID' },
    { key: 'article_id', label: 'Article ID' },
  ],
  'theology-user-state': [
    { key: 'user_id', label: 'User' },
    { key: 'state_key', label: 'Key' },
    { key: 'value', label: 'Value' },
  ],
  'theology-verse-embeddings': [
    { key: 'verse_ref', label: 'Verse Ref' },
    { key: 'text_kjv', label: 'Text' },
    { key: 'testament', label: 'Testament' },
  ],
  'theology-lexicon-embeddings': [
    { key: 'strongs_number', label: "Strong's #" },
    { key: 'definition', label: 'Definition' },
    { key: 'language', label: 'Language' },
  ],
  'theology-articles-embeddings': [
    { key: 'article_id', label: 'Article ID' },
    { key: 'title', label: 'Title' },
    { key: 'journal', label: 'Journal' },
  ],
  'scm-embeddings': [
    { key: 'control_id', label: 'Control ID' },
    { key: 'title', label: 'Title' },
    { key: 'framework', label: 'Framework' },
    { key: 'category', label: 'Category' },
    { key: 'priority', label: 'Priority' },
  ],
  'patent-embeddings': [
    { key: 'patent_id', label: 'Patent ID' },
    { key: 'title', label: 'Title' },
    { key: 'assignee', label: 'Assignee' },
    { key: 'jurisdiction', label: 'Jurisdiction' },
    { key: 'topics', label: 'CPC Topics' },
    { key: 'filing_date', label: 'Filing Date' },
  ],
  // fullset schema differs from Tier-1 (no derived topics/filing_date; ids are
  // publication_number) — columns must match the real mapping, per #4612.
  'patent-embeddings-fullset': [
    { key: 'publication_number', label: 'Publication #' },
    { key: 'title', label: 'Title' },
    { key: 'assignee', label: 'Assignee' },
    { key: 'country_code', label: 'Country' },
    { key: 'cpc_codes', label: 'CPC Codes' },
    { key: 'publication_date', label: 'Published' },
  ],
  support: [
    { key: 'ref', label: 'Reference' },
    { key: 'subject', label: 'Subject' },
    { key: 'portal', label: 'Portal' },
    { key: 'status', label: 'Status' },
    { key: 'priority', label: 'Priority' },
  ],
};

/** Datastores that are not yet ingested — show empty state */
export const EMPTY_DATASTORES: string[] = [];

/**
 * PQC (Post-Quantum Cryptography) metadata attributes for embedding records.
 * These optional attributes are added to DynamoDB embedding metadata when
 * ML-DSA-65 signing is enabled via PQC_SIGNING_KEY_ARN.
 */
export interface PqcEmbeddingMetadata {
  pqc_signature?: string;       // Base64-encoded ML-DSA-65 signature
  pqc_key_id?: string;          // KMS key ARN used for signing
  pqc_algorithm?: string;       // "ML_DSA_65"
  pqc_signed_at?: string;       // ISO 8601 UTC timestamp
  pqc_verified?: boolean | null; // Verification result (search responses only)
}

/** PQC attribute keys for DynamoDB embedding metadata */
export const PQC_ATTRIBUTES = [
  { key: 'pqc_signature', label: 'PQC Signature', description: 'Base64-encoded ML-DSA-65 digital signature' },
  { key: 'pqc_key_id', label: 'PQC Key ID', description: 'KMS key ARN used for signing' },
  { key: 'pqc_algorithm', label: 'PQC Algorithm', description: 'Post-quantum signing algorithm (ML_DSA_65)' },
  { key: 'pqc_signed_at', label: 'PQC Signed At', description: 'ISO 8601 UTC timestamp of signing' },
] as const;
